import React from 'react';

import { Building2, Clock, Mail, User } from 'lucide-react';

import { useGetUserInfoQuery } from '../api/auth';
import {
	Badge,
	Card,
	CardBody,
	CardHeader,
	Container,
	PageContent,
	PageHeader,
	Section,
	Skeleton,
	Typography,
} from '../components/common';
import { useAuth } from '../providers/AppAuthProvider';

const UserProfilePage: React.FC = () => {
	const { user: authUser } = useAuth();
	const {
		data: userInfo,
		isLoading,
		error,
	} = useGetUserInfoQuery(undefined, {
		skip: !authUser, // Skip if not authenticated
	});

	// Use userInfo if available, otherwise fall back to authUser
	const displayUser = userInfo?.success
		? {
				id: userInfo.user_id,
				email: userInfo.email,
				name: userInfo.name,
				enterprise_id: userInfo.enterprise_id,
				timezone: userInfo.timezone,
				roles: authUser?.roles || [],
				permissions: authUser?.permissions || [],
			}
		: authUser;

	if (isLoading) {
		return (
			<Container maxWidth="4xl" padding="lg">
				<PageHeader title="Profile" variant="title" />
				<PageContent>
					<Card>
						<CardBody>
							<Skeleton height={200} />
						</CardBody>
					</Card>
				</PageContent>
			</Container>
		);
	}

	if (error || !displayUser) {
		return (
			<Container maxWidth="4xl" padding="lg">
				<PageHeader title="Profile" variant="title" />
				<PageContent>
					<Card>
						<CardBody>
							<Typography variant="body1" color="error">
								Failed to load user profile. Please try again later.
							</Typography>
						</CardBody>
					</Card>
				</PageContent>
			</Container>
		);
	}

	const formatDate = (dateString?: string) => {
		if (!dateString) return 'N/A';
		try {
			return new Date(dateString).toLocaleString();
		} catch {
			return dateString;
		}
	};

	return (
		<Container maxWidth="4xl" padding="lg">
			<PageHeader title="Profile" variant="title" />
			<PageContent>
				<Section>
					<Card>
						<CardHeader>
							<Typography variant="h2">User Information</Typography>
						</CardHeader>
						<CardBody>
							<div className="space-y-6">
								{/* Name */}
								<div className="flex items-start gap-4">
									<div className="flex-shrink-0">
										<div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
											<User className="w-6 h-6 text-purple-600" />
										</div>
									</div>
									<div className="flex-1">
										<Typography variant="body2" color="muted" className="mb-1">
											Name
										</Typography>
										<Typography variant="body1" weight="medium">
											{displayUser.name || 'N/A'}
										</Typography>
									</div>
								</div>

								<div className="border-t border-neutral-200"></div>

								{/* Email */}
								<div className="flex items-start gap-4">
									<div className="flex-shrink-0">
										<div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
											<Mail className="w-6 h-6 text-blue-600" />
										</div>
									</div>
									<div className="flex-1">
										<Typography variant="body2" color="muted" className="mb-1">
											Email
										</Typography>
										<Typography variant="body1" weight="medium">
											{displayUser.email || 'N/A'}
										</Typography>
									</div>
								</div>

								<div className="border-t border-neutral-200"></div>

								{/* Enterprise ID */}
								<div className="flex items-start gap-4">
									<div className="flex-shrink-0">
										<div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
											<Building2 className="w-6 h-6 text-green-600" />
										</div>
									</div>
									<div className="flex-1">
										<Typography variant="body2" color="muted" className="mb-1">
											Organization ID
										</Typography>
										<Typography
											variant="body1"
											weight="medium"
											className="font-mono text-sm"
										>
											{displayUser.enterprise_id || 'N/A'}
										</Typography>
									</div>
								</div>

								<div className="border-t border-neutral-200"></div>

								{/* Timezone */}
								<div className="flex items-start gap-4">
									<div className="flex-shrink-0">
										<div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
											<Clock className="w-6 h-6 text-orange-600" />
										</div>
									</div>
									<div className="flex-1">
										<Typography variant="body2" color="muted" className="mb-1">
											Timezone
										</Typography>
										<Typography variant="body1" weight="medium">
											{displayUser.timezone || 'UTC'}
										</Typography>
									</div>
								</div>
							</div>
						</CardBody>
					</Card>
				</Section>

				<Section>
					<Card>
						<CardHeader>
							<Typography variant="h2">Roles & Permissions</Typography>
						</CardHeader>
						<CardBody>
							<div className="space-y-4">
								{/* Roles */}
								<div>
									<Typography variant="body2" color="muted" className="mb-3">
										Roles
									</Typography>
									<div className="flex flex-wrap gap-2">
										{displayUser.roles && displayUser.roles.length > 0 ? (
											displayUser.roles.map((role) => (
												<Badge
													key={role}
													variant={
														role === 'super_admin'
															? 'error'
															: role === 'admin' || role === 'enterprise_admin'
																? 'warning'
																: 'default'
													}
												>
													{role.replace('_', ' ').toUpperCase()}
												</Badge>
											))
										) : (
											<Typography variant="body2" color="muted">
												No roles assigned
											</Typography>
										)}
									</div>
								</div>

								<div className="border-t border-neutral-200"></div>

								{/* Permissions */}
								<div>
									<Typography variant="body2" color="muted" className="mb-3">
										Permissions
									</Typography>
									<div className="flex flex-wrap gap-2">
										{displayUser.permissions &&
										displayUser.permissions.length > 0 ? (
											displayUser.permissions.map((permission) => (
												<Badge key={permission} variant="default">
													{permission}
												</Badge>
											))
										) : (
											<Typography variant="body2" color="muted">
												No permissions assigned
											</Typography>
										)}
									</div>
								</div>
							</div>
						</CardBody>
					</Card>
				</Section>

				{userInfo?.retrieved_at && (
					<Section>
						<Card>
							<CardHeader>
								<Typography variant="h2">Account Information</Typography>
							</CardHeader>
							<CardBody>
								<div className="space-y-4">
									<div className="flex items-center justify-between">
										<Typography variant="body2" color="muted">
											Profile Last Updated
										</Typography>
										<Typography variant="body1" weight="medium">
											{formatDate(userInfo.retrieved_at)}
										</Typography>
									</div>
								</div>
							</CardBody>
						</Card>
					</Section>
				)}
			</PageContent>
		</Container>
	);
};

export default UserProfilePage;
